import { calculateBadges, FREQUENCY_BADGES, VOLUME_BADGES } from '@shared/client-types';
import { computeHydrationPlan } from '@/lib/hydrationGoals';

export type YesterdayBadgeRow = {
  emoji: string;
  title: string;
  subtitle: string;
  action: string;
  /** Designed artwork for volume/frequency badges */
  imageSrc?: string;
};

export type YesterdayInput = {
  totalVolume: number;
  targetVolume: number;
  frequencyScore?: string | number;
  missCount?: number;
  successfulIntakes?: number;
  /** Planned / expected reminder count for the day (denominator for volume %) */
  expectedReminders?: number;
  totalReminders?: number;
  volumeBadgeName?: string | null;
  frequencyBadgeName?: string | null;
  volumeBadgePercent?: string | number | null;
  badgesFinalized?: boolean | null;
  activeHoursStart?: string;
  activeHoursEnd?: string;
  reminderInterval?: number;
};

function plannedIntervalsForYesterday(input: YesterdayInput): number {
  const fromInput = Math.max(input.expectedReminders ?? 0, input.totalReminders ?? 0);
  if (fromInput > 0) return fromInput;
  return Math.max(
    computeHydrationPlan({
      activeHoursStart: input.activeHoursStart,
      activeHoursEnd: input.activeHoursEnd,
      reminderInterval: input.reminderInterval,
    }).totalReminders,
    1
  );
}

/**
 * Resolve yesterday's volume + frequency badges using the same inputs as analytics.
 * Prefers frozen daily_stats names/percents when present; otherwise recalculates with
 * expected-volume formula (not totalVolume/targetVolume).
 *
 * Yesterday is always a complete day. Zero logs cannot freeze Frequency on Rhythm.
 */
export function resolveYesterdayBadges(input: YesterdayInput) {
  const plannedIntervals = plannedIntervalsForYesterday(input);
  const volume = input.totalVolume ?? 0;
  const storedMiss = input.missCount ?? 0;
  const successfulIntakes = input.successfulIntakes;
  const emptyDay = volume <= 0 && (successfulIntakes == null || successfulIntakes <= 0);
  const inferredMisses = emptyDay
    ? plannedIntervals
    : successfulIntakes != null
      ? Math.max(0, plannedIntervals - successfulIntakes)
      : 0;
  const missCount = Math.max(storedMiss, inferredMisses);

  const badgeResult = calculateBadges({
    dailyGoal: input.targetVolume,
    totalReminderIntervals: plannedIntervals,
    completedIntervals: plannedIntervals,
    totalWaterLogged: volume,
    missCount,
  });

  const frozenRhythmOnEmptyDay = emptyDay && input.frequencyBadgeName === 'Rhythm';
  const volumeBadge =
    (input.badgesFinalized &&
      input.volumeBadgeName &&
      VOLUME_BADGES.find((badge) => badge.name === input.volumeBadgeName)) ||
    badgeResult.volume;
  const frozenFrequency =
    input.badgesFinalized && !frozenRhythmOnEmptyDay && input.frequencyBadgeName
      ? FREQUENCY_BADGES.find((badge) => badge.name === input.frequencyBadgeName)
      : undefined;
  const frequencyBadge = frozenFrequency || badgeResult.frequency;
  const volumePct =
    input.volumeBadgePercent != null && input.volumeBadgePercent !== ''
      ? Number(input.volumeBadgePercent)
      : badgeResult.volumePercentage;

  return { volumeBadge, frequencyBadge, volumePct, missCount: badgeResult.missCount, badgeResult };
}

export function buildYesterdayBadgeRows(input: YesterdayInput): YesterdayBadgeRow[] {
  const { volumeBadge, frequencyBadge, volumePct, missCount } = resolveYesterdayBadges(input);

  const freqScore =
    typeof input.frequencyScore === 'string'
      ? parseFloat(input.frequencyScore) || 0
      : input.frequencyScore || 0;

  return [
    {
      emoji: volumeBadge.emoji,
      title: volumeBadge.name,
      subtitle: `Volume · ${Math.round(volumePct)}% of expected`,
      imageSrc: volumeBadge.imageSrc,
      action:
        volumePct >= 95
          ? 'Volume goal met — maintain your sip size.'
          : `Tomorrow: aim for ${Math.round(input.targetVolume / 1000)}L with larger logs.`,
    },
    {
      emoji: frequencyBadge.emoji,
      title: frequencyBadge.name,
      subtitle: `Frequency · ${frequencyBadge.description}`,
      imageSrc: frequencyBadge.imageSrc,
      action:
        freqScore >= 80
          ? 'Keep responding to reminders at the same pace.'
          : 'Tomorrow: answer the next 3 reminders within 10 minutes.',
    },
  ];
}
